import { useCallback, useEffect, useRef, useState } from 'react';
import { useMarkReleaseViewedMutation } from '../../../../../redux/api/fymAPI';
import { useIsDemoAccount } from '../../../../accountSelector/useIsDemoAccount';
import { isDemoAccountId } from '../../../../accountSelector/demoAccounts';

interface ReleaseViewMark {
  /** The base time of "Just made" on this visit (ISO); null until the view mark answered. */
  since: string | null;
  /** True when the user never opened the release before this visit. */
  isFirstVisit: boolean;
  /** "Mark all as seen": "Just made" empties for the rest of the visit. */
  markAllSeen: () => void;
}

/**
 * The view mark of the Assets tab (owner answer 4: per user). On open the page sends `POST
 * .../releases/:uuid/viewed` once; the answer's `previousViewedAt` is the base of "Just made" for
 * the whole visit, so the items made since the last visit stay marked while the user looks at them.
 * The demo artist is read only, so the page sends no view mark for it and keeps the 7-day base of
 * a first visit. A refused call keeps that base too.
 */
export function useReleaseViewMark(input: { accountId: number; catalogItemUuid: string; now: Date }): ReleaseViewMark {
  const { accountId, catalogItemUuid, now } = input;
  const [markViewed] = useMarkReleaseViewedMutation();
  // The id check also holds while the artist list loads.
  const isDemoActive = useIsDemoAccount();
  const isDemo = isDemoActive || isDemoAccountId(accountId);
  const [since, setSince] = useState<string | null>(null);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (sent.current === catalogItemUuid) {
      return;
    }
    sent.current = catalogItemUuid;
    const firstVisitBase = new Date(now.getTime() - 7 * 86_400_000).toISOString();
    // No write for the demo artist: a 403 M24_DEMO_READ_ONLY toast must not show in the tour.
    if (isDemo) {
      setIsFirstVisit(true);
      setSince(firstVisitBase);
      return;
    }
    markViewed({ accountId, catalogItemUuid })
      .unwrap()
      .then((answer) => {
        setIsFirstVisit(answer.previousViewedAt === null);
        setSince(answer.previousViewedAt ?? firstVisitBase);
      })
      .catch(() => {
        setIsFirstVisit(true);
        setSince(firstVisitBase);
      });
  }, [accountId, catalogItemUuid, isDemo, markViewed, now]);

  const markAllSeen = useCallback(() => setSince(new Date().toISOString()), []);

  return { since, isFirstVisit, markAllSeen };
}
